'use client';

import classNames from 'classnames';
import { RecaptchaDisclaimer, RecaptchaFormChallenge, RecaptchaFormProvider } from 'google-recaptcha/components';
import Link from 'next/link';
import { JSX } from 'react';

import { Button } from '@videoblocks/storywind';

import { useFormValidation } from '../../../_shared/hooks/useFormValidation';
import { AgreementText } from '../../../_shared/sign-up/AgreementText';
import { PasswordField } from '../../../_shared/sign-up/PasswordField';
import { DEFAULT_SUBMIT_TEXT, useSignUpContext } from '../../../_shared/sign-up/SignUpContext';
import { SignUpError } from '../../../_shared/sign-up/SignUpError';
import { SignUpParams } from '../../../_shared/sign-up/SignUpParams';
import { buildLoginHref } from '../../../_shared/sign-up/buildLoginHref';
import { useReportSignUpFormStarted } from '../../../_shared/sign-up/useReportSignUpFormStarted';
import { useSubmitSignUpForm } from '../../../_shared/sign-up/useSubmitSignUpForm';

export default function SignUpForm({ planCode, redirectAfterRegister, externalAuthId }: SignUpParams): JSX.Element {
    const { error, submitText } = useSignUpContext();
    const { isFormValid, errors, touched, focused, refs, handleInputChange, handleBlur, handleFocus } =
        useFormValidation();

    const INPUT_CLASS =
        'w-full border rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-700';

    const loginHref = buildLoginHref({ planCode, redirectAfterRegister, externalAuthId });
    const recaptcha = useSubmitSignUpForm({ planCode, redirectAfterRegister, externalAuthId });
    const reportFormStarted = useReportSignUpFormStarted();
    // Submittable only when fields are valid AND the reCAPTCHA checkbox is solved; drive both
    // the disabled state and the styling from this one value so they can't disagree.
    const canSubmit = isFormValid && recaptcha.canSubmit;
    return (
        <RecaptchaFormProvider value={recaptcha}>
            <form
                onSubmit={recaptcha.handleSubmit}
                onFocusCapture={reportFormStarted}
                aria-label="Registration Form"
                className="space-y-4"
            >
                <SignUpError error={error} />

                <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-1">
                        Email
                    </label>
                    <input
                        ref={refs.emailInputRef}
                        id="email"
                        name="email"
                        type="email"
                        placeholder="Enter your email"
                        autoComplete="email"
                        required
                        aria-invalid={touched.email && errors.email ? true : undefined}
                        aria-describedby={touched.email && errors.email ? 'email-error' : undefined}
                        onChange={handleInputChange}
                        onFocus={() => handleFocus('email')}
                        onBlur={() => handleBlur('email')}
                        className={classNames(INPUT_CLASS, {
                            'border-gray-700': !errors.email || !touched.email,
                            'border-red-500': errors.email && touched.email,
                        })}
                    />
                    <div aria-live="polite">
                        {touched.email && errors.email && (
                            <div id="email-error" className="mt-1 text-sm text-red-600">
                                {errors.email}
                            </div>
                        )}
                    </div>
                </div>

                <PasswordField
                    id="password"
                    // eslint-disable-next-line react-hooks/refs -- ref originates from useFormValidation, not created locally
                    inputRef={refs.passwordInputRef}
                    error={errors.password}
                    touched={touched.password}
                    focused={focused.password}
                    onChange={handleInputChange}
                    onFocus={() => handleFocus('password')}
                    onBlur={() => handleBlur('password')}
                    inputClassName={INPUT_CLASS}
                    defaultBorderClassName="border-gray-700"
                    labelClassName="text-sm font-semibold text-gray-700 mb-1"
                />

                <div className="mb-6">
                    <div className="flex items-start">
                        <input
                            // eslint-disable-next-line react-hooks/refs
                            ref={refs.checkboxRef}
                            id="agreement-checkbox"
                            name="agreement-checkbox"
                            type="checkbox"
                            role="checkbox"
                            required
                            aria-invalid={touched.checkbox && errors.checkbox ? true : undefined}
                            aria-describedby={
                                touched.checkbox && errors.checkbox ? 'agreement-checkbox-error' : undefined
                            }
                            onChange={handleInputChange}
                            onFocus={() => handleFocus('checkbox')}
                            onBlur={() => handleBlur('checkbox')}
                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="agreement-checkbox" className="ml-3 text-sm text-gray-700">
                            <AgreementText />
                        </label>
                    </div>
                    <div aria-live="polite">
                        {touched.checkbox && errors.checkbox && (
                            <div id="agreement-checkbox-error" className="mt-1 text-sm text-red-600">
                                {errors.checkbox}
                            </div>
                        )}
                    </div>
                </div>

                <RecaptchaFormChallenge />

                {/* Sighted users read "Processing..." off the button; this announces it. */}
                <div role="status" className="sr-only">
                    {submitText === DEFAULT_SUBMIT_TEXT ? '' : submitText}
                </div>

                <Button
                    role="button"
                    type="submit"
                    disabled={!canSubmit}
                    // Safari leaves a clicked button unfocused; focusing it keeps VoiceOver here to hear the error alert.
                    onClick={(event) => event.currentTarget.focus()}
                    className={classNames('w-full py-3 px-4 rounded font-medium transition-colors cursor-pointer', {
                        'bg-gray-300 text-gray-500 cursor-not-allowed': !canSubmit,
                        'bg-yellow-400 hover:bg-yellow-600 text-gray-900': canSubmit,
                    })}
                >
                    {/* VoiceOver drops the label when a focused button's own text changes, so the name stays fixed. */}
                    <span aria-hidden="true">{submitText}</span>
                    <span className="sr-only">{DEFAULT_SUBMIT_TEXT}</span>
                </Button>

                <RecaptchaDisclaimer className="text-center" />

                <div className="text-center mt-6">
                    <p className="text-sm text-gray-700">
                        Already have an account?{' '}
                        <Link className="text-blue-700 underline cursor-pointer" href={loginHref}>
                            Log in
                        </Link>
                    </p>
                </div>
            </form>
        </RecaptchaFormProvider>
    );
}
